import React, { useEffect, useState } from "react";

import { faSearch, faUpload } from "@fortawesome/pro-regular-svg-icons";

// components
import EmployeeCard from "../../components.smart/EmployeeCard/EmployeeCard";
import Button from "../../components/Button";
import { Grid, GridCol } from "../../components/Grid";
import Icon from "../../components/Icon/Icon";
import Input from "../../components/Inputs/InputText";
import PageTitle from "../../components/PageTitle";
import Spacer from "../../components/Spacer";
import Table from "../../components/TableLite";
import UploadDocumentModal from "./components/UploadDocumentModal";

import { useRbacPrimaryRolePermissions } from "../../api/hooks";
// hooks
import { useDocuments } from "../../api/hooks/defs/documents";
import useDocumentTable from "./useDocumentTable";
import useDocumentsUserData from "./useDocumentsUserData";

import InfoPageTemplate from "../../pageTemplates/InfoPageTemplate";
import ErrorPage from "../Error";
// styles
import css from "./Documents.module.scss";
import { filterDocuments, useIsCompanyDocumentPage } from "./helpers";
// schema
import { DocumentsData, DocumentsPageProps } from "./schema";

const documentsEmptyState = {
	myFiles: [],
	companyFiles: [],
	signedFiles: [],
	companyMaxSpace: 0,
	companyTotalFilesSize: 0,
};

const Documents = ({ showEmployeeCard = true }: DocumentsPageProps) => {
	const { data: userData, isLoading: isUserDataLoading } = useDocumentsUserData(); // fetch required user data
	const {
		data: documentsData,
		isLoading: isDocumentsLoading,
		error: documentsError,
	} = useDocuments(userData?.employeeID); // fetch user documents data
	const [documents, setDocuments] = useState<DocumentsData>(documentsData || documentsEmptyState); // hold state since we want to modify the data (change filename, delete document)
	const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState("");

	const isCompanyDocumentPage = useIsCompanyDocumentPage();
	const { data: rbacPermissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("documents");

	useEffect(() => {
		setDocuments(documentsData || documentsEmptyState);
	}, [documentsData]);

	const filteredDocuments = filterDocuments(documents, searchValue);

	const {
		columns,
		rows,
		DeleteDocumentModalComponent,
		EditDocumentModalComponent,
		ViewDocumentModalComponent,
		ScanWarningModalComponent,
	} = useDocumentTable(searchValue.length > 0 ? filteredDocuments : documents, userData, setDocuments);

	if (isUserDataLoading || isDocumentsLoading || isRbacLoading) {
		return <div />;
	}

	if (documentsError?.response?.status) {
		return <ErrorPage code={documentsError.response.status} />;
	}

	const canUpload = !isCompanyDocumentPage || rbacPermissions?.includes("documents:uploadCompanyDocuments");

	const actionButtons = canUpload
		? [
				<Button
					testid="documentUpload"
					onClick={() => setShowUploadModal(true)}
					color="green"
					className={css.uploadButton}
					key={"uploadButton"}
				>
					<Icon icon={faUpload} size="small" className={css.uploadIcon} />
					Upload Documents
				</Button>,
			]
		: [];

	const toolbarActionRow = (
		<Input
			className={css.searchInput}
			value={searchValue}
			onChange={setSearchValue}
			placeholder="Search tags"
			icon={faSearch}
			clearable
		/>
	);

	const mainContent = (
		<Grid className={css.Grid}>
			<GridCol>
				<Table theme="vJul23" columns={columns} rows={rows} />
			</GridCol>
		</Grid>
	);

	const CompanyDocument = (
		<>
			<PageTitle title={isCompanyDocumentPage ? "Company Documents" : "My Documents"} />
			<Grid className={css.Grid} cols={12} gap={3} padding={7}>
				<GridCol span={2} className={!showEmployeeCard ? css.hide : ""}>
					<EmployeeCard employeeID={userData.employeeID} />
				</GridCol>
				<GridCol span={showEmployeeCard ? 10 : 12}>
					<div className={css.tableUtilsContainer}>
						{canUpload && actionButtons}
						<div className={css.spacer} />
						{toolbarActionRow}
					</div>
					<Spacer height={2} />
					<Table theme="vJul23" columns={columns} rows={rows} />
				</GridCol>
			</Grid>
		</>
	);

	return (
		<>
			{isCompanyDocumentPage ? (
				CompanyDocument
			) : (
				<InfoPageTemplate
					actionButtons={actionButtons}
					toolbarActionRow={toolbarActionRow}
					toolbarInformationRow={<></>}
					drawer={undefined}
					mainContent={mainContent}
					pageTitle={"My Documents"}
					employeeID={showEmployeeCard ? userData.employeeID : undefined}
				/>
			)}
			{DeleteDocumentModalComponent}
			{EditDocumentModalComponent}
			{ViewDocumentModalComponent}
			{ScanWarningModalComponent}
			<UploadDocumentModal
				show={showUploadModal}
				onClose={() => setShowUploadModal(false)}
				userData={userData}
				setDocuments={setDocuments}
				documents={documents}
			/>
		</>
	);
};

export default Documents;
